import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from '../lib/axiosInstance';
import { useGenericStore } from '../store';
import {
  AppErrorResponse,
  BaseModel,
  DepartmentModel,
  LineModel,
  LocationModel,
  MaterialIssuanceFilter,
  PartnerModel,
  ProductModel,
  RecordFile,
  UnitOfMeasureModel,
  WarehouseModel,
} from '../types';
import { ChangePassword, User } from '../types/auth.model';
import { CanvassRecord } from '../types/Canvass';
import { DeliveryReceiptRecord } from '../types/DeliveryReceipt';
import { baseUrl } from '../lib/utils';

type MutationArgs<T> = {
  id?: string;
  form: T;
};

type DeleteMutationArgs = {
  id: string;
};

export const useBaseStore = useGenericStore<BaseModel | null>(null);

export const useWarehouseStore = useGenericStore<WarehouseModel | null>(null);

export const useLocationStore = useGenericStore<LocationModel | null>(null);

export const usePartnerStore = useGenericStore<PartnerModel | null>(null);
export const useDepartmentStore = useGenericStore<DepartmentModel | null>(null);
export const useLineStore = useGenericStore<LineModel | null>(null);
export const useUserStore = useGenericStore<User | null>(null);
export const useChangePasswordStore = useGenericStore<ChangePassword | null>(
  null,
);

export const useUnitOfMeasureStore = useGenericStore<UnitOfMeasureModel | null>(
  null,
);

export const useProductStore = useGenericStore<ProductModel | null>(null);

export const useCanvassStore = useGenericStore<CanvassRecord | null>(null);

export const useDeliveryReceiptStore =
  useGenericStore<DeliveryReceiptRecord | null>(null);

export const useMaterialIssuanceReportStore =
  useGenericStore<MaterialIssuanceFilter | null>(null);

export function useGetMe() {
  return useQuery<User, AxiosError<AppErrorResponse>>({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<User>('/users/me');
      return data;
    },
    retry: false,
  });
}
export function useFetchData<T>(url: string, params?: any) {
  return useQuery<T, AxiosError<AppErrorResponse>>({
    queryKey: [url, params],
    queryFn: async () => {
      const { data } = await axiosInstance.get<T>(url, { params });
      return data;
    },
  });
}

export function useFetchFile(
  url: string,
  urlPath: string,
  params?: any,
  // e.g. { enabled: false } so a record that does not exist yet is not fetched
  options?: { enabled?: boolean },
) {
  return useQuery<RecordFile[], AxiosError<AppErrorResponse>>({
    enabled: options?.enabled ?? true,
    queryKey: [url, params],
    queryFn: async () => {
      const { data } = await axiosInstance.get<RecordFile[]>(url, { params });
      return data.map((item) => ({
        ...item,
        code: `${baseUrl}/${urlPath}/files/` + item.code,
      }));
    },
  });
}

export function useDownLoadFile(url: string, params?: any) {
  return useQuery<any, AxiosError<AppErrorResponse>>({
    queryKey: [url, params],
    queryFn: async () => {
      const data = await axiosInstance.get(url, {
        params,
        responseType: 'blob',
      });
      return await data.data.arrayBuffer();
    },
  });
}

export function useCreateData<T>(API: string) {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError<AppErrorResponse>, T, unknown>({
    mutationFn: async (formData: T) => {
      return await axiosInstance.post<T, AxiosError<AppErrorResponse>>(
        API,
        formData,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
}

export function useUpdateData<T>(API: string) {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    AxiosError<AppErrorResponse>,
    MutationArgs<T> & { id: string | undefined },
    unknown
  >({
    mutationFn: async ({
      id,
      form,
    }: MutationArgs<T> & { id: string | undefined }) => {
      return await axiosInstance.put<T, AxiosError<AppErrorResponse>>(
        `${API}/${id}`,
        form,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
}

export function useUpdateStatus<T>(API: string) {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    AxiosError<AppErrorResponse>,
    MutationArgs<T> & { id: string | undefined },
    unknown
  >({
    mutationFn: async ({
      id,
      form,
    }: MutationArgs<T> & { id: string | undefined }) => {
      return await axiosInstance.put<T, AxiosError<AppErrorResponse>>(
        `${API}`,
        form,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
}

export function useDeleteData<T>(API: string) {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    AxiosError<AppErrorResponse>,
    DeleteMutationArgs,
    unknown
  >({
    mutationFn: async ({ id }: DeleteMutationArgs) => {
      if (!id) throw new Error('ID is required for delete mutation');
      return await axiosInstance.delete<T, AxiosError<AppErrorResponse>>(
        `${API}/${id}`,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
}

export function useReactivateData<T>(API: string) {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    AxiosError<AppErrorResponse>,
    DeleteMutationArgs,
    unknown
  >({
    mutationFn: async ({ id }: DeleteMutationArgs) => {
      if (!id) throw new Error('ID is required for reactivate mutation');
      return await axiosInstance.put<T, AxiosError<AppErrorResponse>>(
        `${API}/${id}/reactivate`,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
}
